/**
 * The plan header's summary strip (RP-6.4, RP-7.1, §4.4).
 *
 * Six figures, in the order the approved mockup puts them: Total Hours · Indicative
 * Revenue · Blended Rate · Margin of record · vs Card Margin · vs Estimate.
 *
 * Two things this component exists to get right:
 *
 *   - **Blended Rate is labeled "revenue ÷ hours"**, in the tile's tooltip, because the
 *     source workbooks print an unweighted average of the rate column and the two differ
 *     by $10.38/hr on TBOS — $250,711 across 24,140 hours. RP-6.4 bans the unweighted
 *     figure and the label is how a planner who knows the spreadsheet learns which one
 *     this is. It was a sub-caption on the tile until 2026-10-22; formulas now go in the
 *     tooltip, not a second line ("Say it once", docs/design-system.md), and a tile with
 *     nothing to add renders no note at all.
 *   - **Amber is a warning ABOVE the floor**, never a shade of failure below it, so red
 *     maps 1:1 to "this needs approval" and the badge and the approval gate never say different
 *     things.
 *
 * Cost never appears here. Margin percentages and floor status are safe for [ACCT]; the
 * dollars behind them are stripped server-side and are simply absent from the payload.
 * The Labor Category baseline (RP-7.5) is a margin %, but it narrows the named Resources'
 * blended rate, so the server sends it only to ADMIN / FIN / RM.
 */

import { cn } from "@/lib/utils";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

interface Tile {
  label: string;
  value: string;
  note: string;
  tone?: "default" | "warn" | "bad" | "good";
  title?: string;
  /** A thin planned-vs-estimated progress bar under the value (vs Estimate tile only). */
  bar?: { widthPercent: number; tone: "good" | "warn" | "bad" };
}

function money(value: string | null): string {
  if (value === null) return "—";
  const negative = value.startsWith("-");
  const [whole = "0", frac = "00"] = value.replace("-", "").split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "−" : ""}$${grouped}.${frac}`;
}

function hours(value: string): string {
  const [whole = "0"] = value.split(".");
  const negative = whole.startsWith("-");
  return `${negative ? "−" : ""}${whole.replace("-", "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

function signedPercent(value: string | null): string {
  if (value === null) return "—";
  return value.startsWith("-") ? `−${value.slice(1)}%` : `+${value}%`;
}

function signedHours(value: string | null): string {
  if (value === null) return "0 h";
  const negative = value.startsWith("-");
  return `${negative ? "−" : "+"}${hours(value.replace("-", ""))} h`;
}

/**
 * RP-7.5: how far the named Resources' Internal Rates move margin from the Labor Category
 * baseline, as signed points — or null when there is nothing to compare. The baseline only
 * reaches readers holding `viewResourceInternalRates`; for everyone else it is absent.
 */
function baselineGap(plan: ResourcePlanDetailView): { points: string; baseline: string } | null {
  const baseline = plan.laborCategoryBaselineMarginPercent;
  if (baseline == null || plan.marginOfRecordPercent === null) return null;
  const gap = Number(plan.marginOfRecordPercent) - Number(baseline);
  const points = Math.abs(gap).toFixed(2);
  // A Resource-priced line with no hours yet moves nothing — no gap worth a note.
  if (points === "0.00") return null;
  return { points: gap < 0 ? `−${points}` : `+${points}`, baseline };
}

export function PlanSummaryStrip({ plan }: { plan: ResourcePlanDetailView }) {
  const marginTone =
    plan.marginStatus === "red" ? "bad" : plan.marginStatus === "amber" ? "warn" : "good";
  const gap = baselineGap(plan);
  const baselineTitle =
    gap === null
      ? ""
      : ` At Labor Category cost it would be ${gap.baseline}% — the gap is what the named Resources' Internal Rates do to the deal (RP-7.5).`;
  // RP-22.2: on a Staffed plan, what the people who actually hold the work make of it. Only
  // ADMIN / FIN / RM are sent it; for everyone else the key is absent.
  const staffed = plan.staffedMarginPercent;
  const unstaffed = plan.staffedUnstaffedLineCount ?? 0;
  const staffedTitle =
    staffed == null
      ? ""
      : ` Staffed Margin ${staffed}%: the same hours priced at whoever holds each Plan Line on the Solution now (RP-22.2).${
          unstaffed > 0
            ? ` ${unstaffed} Plan Line${unstaffed === 1 ? " has" : "s have"} hours nobody holds, costed at Labor Category.`
            : ""
        }`;

  const estimateTone =
    plan.estimateBand === "red"
      ? "bad"
      : plan.estimateBand === "warn"
        ? "warn"
        : plan.estimateBand === "reconciled"
          ? "good"
          : "default";

  const excludedLineCount = plan.estimatedHoursLines.filter((line) => !line.included).length;

  const estimatedNum = plan.estimatedHours === null ? 0 : Number(plan.estimatedHours);
  const plannedNum = Number(plan.totalHours);
  const estimateBar: Tile["bar"] =
    plan.estimateBand === "no-estimate"
      ? undefined
      : {
          widthPercent:
            estimatedNum > 0
              ? Math.min(plannedNum / estimatedNum, 1) * 100
              : plannedNum > 0
                ? 100
                : 0,
          tone:
            plan.estimateBand === "reconciled"
              ? "good"
              : plan.estimateBand === "warn"
                ? "warn"
                : "bad",
        };

  const tiles: Tile[] = [
    {
      label: "Total Hours",
      // EFFECTIVE hours once a ramp is applied (RP-4.3: "effective hours drive all
      // totals"). A ramped plan says so in the note and names the entered figure, because
      // a header total that silently differs from the sum of the grid is the kind of thing
      // that makes someone stop trusting the arithmetic.
      value: hours(plan.totalHours),
      note:
        plan.ramped === true
          ? `${hours(plan.enteredHours ?? plan.totalHours)} entered · ramped`
          : "",
      title:
        plan.ramped === true
          ? "Effective hours — entered hours × each month's ramp factor. The ramp row above the grid is where the factors are set (RP-4.3)."
          : undefined,
    },
    {
      label: "Indicative Revenue",
      value: money(plan.indicativeRevenue),
      note: "",
      title:
        "Always derived, on every plan — including a Fixed-Staffed one, where knowing what the hours are worth at our own rates is the whole point (§4.4).",
    },
    {
      label: "Blended Rate",
      value: money(plan.blendedRate),
      note: "",
      title:
        "revenue ÷ hours — weighted. Never the average of the rate column: on the TBOS sheet the unweighted average reads $186.05 against a true $175.67, which would invent $250,711 across 24,140 hours (RP-6.4).",
    },
    {
      label: plan.marginOfRecordKind === "contracted" ? "Contracted Margin" : "Margin of record",
      value: plan.marginOfRecordPercent === null ? "—" : `${plan.marginOfRecordPercent}%`,
      // The floor stays visible: the gap is added to it, never in place of it.
      // On a Staffed plan, Staffed Margin takes the second slot; the baseline stays in the
      // tooltip.
      note:
        staffed != null
          ? `floor ${plan.marginFloorPercent}% · Staffed ${staffed}%`
          : gap === null
            ? `floor ${plan.marginFloorPercent}% · band ${plan.marginAmberBandPoints}`
            : `floor ${plan.marginFloorPercent}% · ${gap.points} pts vs Labor Category cost`,
      tone: marginTone,
      title: `${
        plan.marginStatus === "red"
          ? plan.approvalStatus === "Approved"
            ? `Below the ${plan.marginFloorPercent}% floor, and approved at that margin (RP-17.2).`
            : `Below the ${plan.marginFloorPercent}% floor — Finance or an Admin has to approve before the plan can be Proposed (RP-17.1).`
          : plan.marginStatus === "amber"
            ? `Above the ${plan.marginFloorPercent}% floor but inside the ${plan.marginAmberBandPoints}-point warning band. Approaching the floor — no approval needed.`
            : `Above the ${plan.marginFloorPercent}% floor with room to spare.`
      }${baselineTitle}${staffedTitle}`,
    },
    {
      label: "vs Card Margin",
      value: plan.cardMarginPercent === null ? "—" : `${plan.cardMarginPercent}%`,
      note: "",
      title: `What the standard Project Mix yields at list rates on this Rate Basis. A plan materially below it is either discount-heavy or mix-heavy in the wrong direction (RP-7.1). Measured against the ${plan.rateBasisLabel.replace(/\s*\(.*\)$/, "")} card.`,
    },
    {
      label: "Planned vs Estimated",
      value:
        plan.estimateBand === "no-estimate"
          ? "—"
          : `${hours(plan.totalHours)} / ${hours(plan.estimatedHours ?? "0")} h`,
      note:
        plan.estimateBand === "no-estimate"
          ? "no estimate lines"
          : `${signedHours(plan.estimateVarianceHours)} · ${signedPercent(plan.estimateVariancePercent)}${
              excludedLineCount > 0 ? ` · ${excludedLineCount} excluded` : ""
            }`,
      tone: estimateTone,
      bar: estimateBar,
      title:
        "What the work was sized at, before it was staffed — the total of the lines on the Estimates tab. The estimate never drives the plan; it is only what the plan is checked against.",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border border-border bg-card px-4 py-3 sm:grid-cols-3 lg:grid-cols-6">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className={cn(tile.title !== undefined && "cursor-help")}
          title={tile.title}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {tile.label}
          </div>
          <div
            className={cn(
              "mt-0.5 font-display text-lg font-bold leading-tight tabular-nums",
              tile.tone === "bad" && "text-error-700 dark:text-error-400",
              tile.tone === "warn" && "text-warning-700 dark:text-warning-400",
              tile.tone === "good" && "text-success-700 dark:text-success-400",
            )}
          >
            {tile.value}
          </div>
          {tile.bar !== undefined && (
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full",
                  tile.bar.tone === "bad" && "bg-error",
                  tile.bar.tone === "warn" && "bg-warning",
                  tile.bar.tone === "good" && "bg-success",
                )}
                style={{ width: `${tile.bar.widthPercent}%` }}
              />
            </div>
          )}
          {tile.note !== "" && <div className="text-xs text-muted-foreground">{tile.note}</div>}
        </div>
      ))}
    </div>
  );
}

/**
 * The two-margin line (RP-7.1).
 *
 * Where both exist, they are shown TOGETHER — "Contracted 48.1% · Indicative 59.5% · the
 * fee is 19% below the hours' value" — so a discount shows up as a number rather than a
 * feeling. A fee ABOVE Indicative Revenue is shown just as plainly: it is not always good
 * news, it can mean the hours are under-scoped.
 */
export function FeeVarianceLine({ plan }: { plan: ResourcePlanDetailView }) {
  if (plan.contractedFee === null) return null;

  const variance = plan.feeVariancePercent;
  // RP-D16: a Monthly fee is per month, but the variance (and the Contracted Margin) is
  // measured over every month in the window, so say so rather than set a monthly figure
  // beside a total one. A version frozen before that fix carries no fee of record, and
  // its variance is still the old one-month figure — so it gets no window label.
  const monthly = plan.contractedFeeBasis === "Monthly" && plan.contractedFeeOfRecord !== undefined;
  const under = variance?.startsWith("-") ?? false;
  const magnitude = variance?.replace("-", "") ?? "0.00";

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
      <span className="font-semibold uppercase tracking-wider text-xs text-muted-foreground">
        Contracted Fee
      </span>
      <span className="font-semibold tabular-nums">{money(plan.contractedFee)}</span>
      <span className="text-muted-foreground">
        ({plan.contractedFeeBasis === "Monthly" ? "per month" : "not-to-exceed ceiling"})
      </span>
      <span aria-hidden className="text-muted-foreground">
        ·
      </span>
      <span className="font-semibold uppercase tracking-wider text-xs text-muted-foreground">
        Fee Variance
      </span>
      <span
        className={cn(
          "font-semibold tabular-nums",
          under ? "text-error-700 dark:text-error-400" : "text-success-700 dark:text-success-400",
        )}
        title={
          under
            ? "The fee sits BELOW what the hours are worth at our own rates — that gap is the discount, as a number."
            : "The fee sits ABOVE what the hours are worth. Not always good news: it can mean the hours are under-scoped (RP-7.1)."
        }
      >
        {plan.feeVariance === null ? "—" : money(plan.feeVariance)}
      </span>
      {monthly && (
        <span className="text-muted-foreground">over the {plan.months.length}-month window</span>
      )}
      {variance !== null && (
        <span className="text-muted-foreground tabular-nums">
          ({under ? "fee is" : "fee is"} {magnitude}% {under ? "below" : "above"} the hours&rsquo;
          value)
        </span>
      )}
      {plan.contractedMarginPercent !== null && plan.indicativeMarginPercent !== null && (
        <span className="ml-auto text-muted-foreground">
          Contracted{" "}
          <span className="font-semibold text-foreground tabular-nums">
            {plan.contractedMarginPercent}%
          </span>{" "}
          · Indicative{" "}
          <span
            className="font-semibold text-foreground tabular-nums"
            title="Diagnostic only — the Indicative Margin never gates a transition. Only what we actually make does (RP-17.1)."
          >
            {plan.indicativeMarginPercent}%
          </span>
        </span>
      )}
    </div>
  );
}
